/**
 * BlankAutoGroupModal: the Products > POD tab's "Group blanks" button, over
 * `inventory.previewBlankGroups` (a dry run) and `inventory.groupBlanksByCode`.
 *
 * Blanks group themselves by product code whenever one is created, renamed,
 * restored or switched to POD. This is the catch-up for the loose ones that
 * were there before: the preview shows what ONE click would do (a new product
 * or the product it joins, how many blanks, and how many change product type),
 * then "Group all" does it. It is read-only on purpose: the product name comes
 * from the code's name in Base garments, so there is nothing to edit here and
 * nothing to tick. "Group all" sends only the codes listed as changing, so it applies
 * exactly what the dialog says it will; the server decides the rest and takes no
 * target org.
 *
 * The preview is read only while the dialog is open and never reused: the loose
 * counts change under it.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { SectionError } from '@/components/shared/SectionError';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { trpc, type RouterOutputs } from '@/lib/trpc';

type BlankGroup = RouterOutputs['inventory']['previewBlankGroups']['groups'][number];

interface BlankAutoGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGrouped: () => void | Promise<void>;
}

/** A code the call groups or renames; anything else is listed as left alone. */
const willChange = (group: BlankGroup): boolean =>
  group.action !== 'skipped' && (group.action === 'created' || group.attached > 0 || group.retitled);

export default function BlankAutoGroupModal({ isOpen, onClose, onGrouped }: BlankAutoGroupModalProps) {
  const { t } = useTranslation('inventory');

  const preview = trpc.inventory.previewBlankGroups.useQuery(undefined, {
    enabled: isOpen,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const mutation = trpc.inventory.groupBlanksByCode.useMutation({
    onSuccess: async (result) => {
      const attached = result.groups.reduce((sum, group) => sum + group.attached, 0);
      toast.success(attached > 0 ? t('blankGroups.done', { count: attached }) : t('blankGroups.doneRenamedOnly'));
      onClose();
      await onGrouped();
    },
    onError: () => {
      toast.error(t('blankGroups.groupError'));
    },
  });

  const { toGroup, leftAlone } = useMemo(() => {
    const groups = preview.data?.groups ?? [];
    return {
      toGroup: groups.filter(willChange),
      leftAlone: groups.filter((group) => !willChange(group)),
    };
  }, [preview.data]);

  const codesToGroup = useMemo(() => toGroup.map((group) => group.code), [toGroup]);

  const reasonText = (group: BlankGroup) =>
    t(`blankGroups.reason.${group.reason ?? 'unknown'}`, { defaultValue: t('blankGroups.reason.unknown') });

  const canGroup = toGroup.length > 0 && !preview.isFetching && !mutation.isPending;

  const handleOpenChange = (open: boolean) => {
    if (!open && !mutation.isPending) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('blankGroups.title')}</DialogTitle>
          <DialogDescription>{t('blankGroups.description')}</DialogDescription>
        </DialogHeader>

        {preview.isLoading && (
          <div role="status" className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            {t('blankGroups.loading')}
          </div>
        )}

        {preview.isError && (
          <SectionError
            variant="compact"
            title={t('blankGroups.loadErrorTitle')}
            body={t('blankGroups.loadErrorBody')}
            retryLabel={t('blankGroups.retry')}
            retrying={preview.isFetching}
            onRetry={() => void preview.refetch()}
          />
        )}

        {preview.data && (
          <div className="space-y-4">
            {toGroup.length === 0 ? (
              <p className="py-2 text-sm text-muted-foreground">{t('blankGroups.empty')}</p>
            ) : (
              <div className="max-h-80 overflow-y-auto rounded-md border">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[22%]">{t('blankGroups.col.code')}</TableHead>
                      <TableHead className="w-[30%]">{t('blankGroups.col.name')}</TableHead>
                      <TableHead className="w-[16%] text-right">{t('blankGroups.col.loose')}</TableHead>
                      <TableHead className="w-[32%]">{t('blankGroups.col.product')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {toGroup.map((group) => (
                      <TableRow key={group.code}>
                        <TableCell className="break-all align-top font-mono text-xs">{group.code}</TableCell>
                        <TableCell className="break-words align-top">{group.titleAfter ?? group.code}</TableCell>
                        <TableCell className="align-top text-right tabular-nums">{group.attached}</TableCell>
                        <TableCell className="break-words align-top">
                          {group.action === 'created'
                            ? t('blankGroups.newProduct')
                            : t('blankGroups.addsTo', { title: group.titleBefore ?? group.titleAfter ?? group.code })}
                          {group.action === 'existing' && group.retitled && (
                            <span className="block text-xs text-muted-foreground">{t('blankGroups.andRenames')}</span>
                          )}
                          {group.retyped > 0 && (
                            <span className="block text-xs text-muted-foreground">
                              {t('blankGroups.retyped', { count: group.retyped })}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {leftAlone.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('blankGroups.leftAloneTitle')}
                </p>
                <p className="text-xs text-muted-foreground">{t('blankGroups.leftAloneBody')}</p>
                <ul className="space-y-1 pt-1">
                  {leftAlone.map((group) => (
                    <li key={group.code} className="flex flex-wrap gap-x-2 text-sm">
                      <span className="break-all font-mono text-xs">{group.code}</span>
                      <span className="min-w-0 text-muted-foreground [overflow-wrap:anywhere]">{reasonText(group)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-muted-foreground">{t('blankGroups.namesNote')}</p>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            {toGroup.length > 0 ? t('blankGroups.cancel') : t('blankGroups.close')}
          </Button>
          <Button onClick={() => mutation.mutate({ codes: codesToGroup })} disabled={!canGroup}>
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                {t('blankGroups.grouping')}
              </>
            ) : (
              t('blankGroups.groupAll')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
